import styled, { css, keyframes } from "styled-components";
import { motion } from "framer-motion";

/* ---------- Container global ---------- */

export const LoaderShell = styled.div`
  width: 100%;
  max-width: 1350px;
  margin: 0 auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  /* Ocupa toda a área visível abaixo do Header (var setada pelo Header).
     Fallback 0px cobre o boot screen, antes do Header existir.
     100dvh resolve o jitter da barra de URL no Safari iOS. */
  min-height: calc(100vh - var(--app-header-height, 0px));
  min-height: calc(100dvh - var(--app-header-height, 0px));
  color: ${({ theme }) => theme.textSecondary};

  @media (max-width: 760px) {
    padding: 16px;
    gap: 12px;
  }
`;

export const SceneWrap = styled.div`
  width: clamp(64px, 12vw, 96px);
  height: clamp(64px, 12vw, 96px);
  position: relative;
`;

export const MessageWrap = styled.div`
  min-height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
  max-width: 380px;
`;

export const Message = styled(motion.div)`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.textSecondary};
  letter-spacing: 0.01em;
  text-align: center;

  @media (max-width: 760px) {
    font-size: 13px;
  }
`;

export const HiddenLabel = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

/* ---------- Spinner circular (SVG) ---------- */

const rotate360 = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const pulseOpacity = keyframes`
  0%, 100% { opacity: 0.55; }
  50% { opacity: 1; }
`;

const reducedBarSlide = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

export const SceneSvg = styled.svg`
  width: 100%;
  height: 100%;
  display: block;
  overflow: visible;
`;

export const SpinnerArc = styled.g`
  transform-origin: 50% 50%;
  transform-box: fill-box;
  animation: ${rotate360} 0.9s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: ${pulseOpacity} 1.6s ease-in-out infinite;
  }
`;

export const ReducedBar = styled.div`
  display: none;
  width: 220px;
  height: 4px;
  border-radius: 999px;
  overflow: hidden;
  background: ${({ theme }) => theme.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.border};
  margin-top: 8px;

  & > span {
    display: block;
    width: 40%;
    height: 100%;
    background: ${({ theme }) => theme.accent};
    border-radius: 999px;
    transform: translateX(-100%);
  }

  @media (prefers-reduced-motion: reduce) {
    display: block;

    & > span {
      animation: ${reducedBarSlide} 1.4s ease-in-out infinite;
    }
  }
`;

/* ---------- Mini spinner (botões/inline) ---------- */

export const MiniSpinnerSvg = styled.svg`
  display: inline-block;
  vertical-align: middle;
  animation: ${rotate360} 0.7s linear infinite;
  flex-shrink: 0;

  @media (prefers-reduced-motion: reduce) {
    animation: ${pulseOpacity} 1.6s ease-in-out infinite;
  }
`;

/* ---------- Wood-grain skeleton ---------- */

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const woodGradient = ({ theme }) => css`
  background: linear-gradient(
    100deg,
    ${theme.surfaceAlt} 0%,
    ${theme.surfaceAlt} 18%,
    ${theme.woodLight}40 35%,
    ${theme.woodMid}30 50%,
    ${theme.woodLight}40 65%,
    ${theme.surfaceAlt} 82%,
    ${theme.surfaceAlt} 100%
  );
  background-size: 400% 100%;
  animation: ${shimmer} 2.2s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    background: ${theme.surfaceAlt};
  }
`;

export const WoodBox = styled.div`
  ${woodGradient}
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.border};
  width: ${({ $width }) => $width || "100%"};
  height: ${({ $height }) => $height || "16px"};
`;

export const WoodRowShell = styled.div`
  display: grid;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  grid-template-columns: ${({ $columns }) => `repeat(${$columns || 4}, 1fr)`};

  &:last-child {
    border-bottom: 0;
  }
`;

export const WoodCardShell = styled.div`
  ${woodGradient}
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.border};
  height: ${({ $height }) => $height || "96px"};
  width: 100%;
`;

export const WoodTableShell = styled.div`
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  overflow: hidden;
  background: ${({ theme }) => theme.surface};
`;

export const WoodTableHeader = styled.div`
  display: grid;
  gap: 8px;
  padding: 12px;
  background: ${({ theme }) => theme.surfaceAlt};
  border-bottom: 1px solid ${({ theme }) => theme.border};
  grid-template-columns: ${({ $columns }) => `repeat(${$columns || 4}, 1fr)`};
`;
